/** @jsx jsx */

import { ReactNode, useRef } from 'react';
import { Icon } from '@zapier/zinnia-icons';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend as ChartJSLegend,
} from 'chart.js';
import { getRelativePosition } from 'chart.js/helpers';
import { Line } from 'react-chartjs-2';

import { css, jsx } from '@emotion/react';
import { Button, Colors, Grid, SkeletonBlock } from '@zapier/design-system';

const Styles = {
  graphWrapper: css`
    display: flex;
    align-items: center;
    justify-content: center;
    align-self: stretch;
    width: 100%;
  `,
  skeletonWrapper: css`
    width: 100%;
  `,
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  ChartJSLegend
);

type Props = {
  colors: { [value: string]: string };
  data: {
    datasets: { [value: string]: Array<number> };
    labels: Array<string>;
    periods: Array<{ start_date: string; end_date: string }>;
  };
  fillColorTransparent: boolean;
  formatLabel: (opts: string) => string;
  isAnimated: boolean;
  isLoading: boolean;
  onClickGraph: (index: number) => boolean | void;
  onClickRefresh: () => void;
  sidebar: ReactNode;
};

const defaultProps = {
  formatLabel: (label: string) => label,
};

function Graph({
  colors,
  data,
  fillColorTransparent,
  formatLabel = defaultProps.formatLabel,
  isAnimated,
  isLoading,
  onClickGraph,
  onClickRefresh,
  sidebar,
}: Props) {
  const chartRef = useRef<ChartJS<'line'>>(null);
  const datasets = Object.keys(colors).map(key => {
    const color = colors[key];

    return {
      backgroundColor: `rgba(${color}, 0.5)`,
      borderColor: `rgba(${color}, 0.8)`,
      data: [...(data?.datasets?.[key] ?? [])],
      fill: !fillColorTransparent,
      label: formatLabel(key),
      lineTension: 0.2,
      pointBackgroundColor: `rgba(${color}, 1)`,
      pointBorderColor: Colors.PrimeWhite,
      pointHoverBackgroundColor: Colors.PrimeWhite,
      pointHoverBorderColor: `rgba(${color}, 1)`,
    };
  });

  return (
    <Grid columns="70% auto" gap={20}>
      <figure css={Styles.graphWrapper}>
        {isLoading ? (
          <div css={Styles.skeletonWrapper} data-testid="graph-loading">
            <SkeletonBlock height="500px" width="100%" />
          </div>
        ) : data ? (
          <Line
            data={{
              datasets,
              labels: data.labels,
            }}
            onClick={(event: any) => {
              if (chartRef.current && onClickGraph) {
                const { x } = getRelativePosition(
                  event.nativeEvent,
                  chartRef.current
                );
                const index = chartRef.current.scales.x.getValueForPixel(x);
                onClickGraph(index);
              }
            }}
            options={{
              animation: { duration: isAnimated ? 1000 : 0 },
              maintainAspectRatio: false,
              responsive: true,
              onHover: event => {
                (event.native.target as HTMLElement).style.cursor = 'pointer';
              },
              plugins: { tooltip: { intersect: false, mode: 'index' } },
            }}
            ref={chartRef}
          />
        ) : (
          <span>No data</span>
        )}
      </figure>
      <Grid columns={1} gap={20}>
        {sidebar}
        <Button onPress={onClickRefresh} iconPosition="start">
          <Icon name="arrowRefresh" />
          Refresh
        </Button>
      </Grid>
    </Grid>
  );
}

Graph.defaultProps = defaultProps;

export default Graph;
